import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';
import { COMPANY, NAV_LINKS } from '../data/site';
import { EASE } from './Reveal';

export function Logo({ light = false }) {
  return (
    <img
      src={light ? '/assets/fm-wordmark-light.png' : '/assets/fm-wordmark-t.png'}
      alt="F&M Umzüge & Entrümpelung – Wir packen das."
      className="h-9 w-auto transition-transform duration-300 hover:scale-[1.03] sm:h-10"
    />
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const solid = scrolled || location.pathname !== '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goTo = (href) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== '/') {
      navigate('/' + href);
      return;
    }
    const el = document.querySelector(href);
    if (el && window.__lenis) {
      window.__lenis.scrollTo(el, { offset: -80 });
    } else if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        solid ? 'bg-white/90 shadow-lg shadow-navy/5 backdrop-blur-xl' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-brand-logo" aria-label="Zur Startseite" onClick={() => setOpen(false)}>
          <Logo light={!solid} />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Hauptnavigation">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              onClick={goTo(l.href)}
              className={`text-sm font-medium tracking-wide transition-colors duration-300 hover:text-brand ${
                solid ? 'text-slate-600' : 'text-slate-200'
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href="#kontakt"
            data-testid="nav-cta-button"
            onClick={goTo('#kontakt')}
            className="group flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand/30 transition-all duration-300 hover:bg-brand-hover hover:shadow-brand/50"
          >
            Angebot anfragen
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          aria-label={open ? 'Menü schließen' : 'Menü öffnen'}
          className={`grid h-11 w-11 place-items-center rounded-xl lg:hidden ${
            solid || open ? 'bg-slate-100 text-navy' : 'bg-white/10 text-white'
          }`}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden border-t border-slate-100 bg-white lg:hidden"
          >
            <nav className="flex flex-col gap-1 px-4 py-6" aria-label="Mobile Navigation">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-${l.testid}`}
                  onClick={goTo(l.href)}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4, ease: EASE }}
                  className="rounded-xl px-4 py-3 font-display text-lg font-semibold text-navy transition-colors hover:bg-slate-50 hover:text-brand"
                >
                  {l.label}
                </motion.a>
              ))}
              <div className="mt-4 flex flex-col gap-3 px-4">
                <a
                  href="#kontakt"
                  data-testid="mobile-nav-cta-button"
                  onClick={goTo('#kontakt')}
                  className="flex items-center justify-center gap-2 rounded-xl bg-brand px-5 py-3.5 font-semibold text-white shadow-lg shadow-brand/30"
                >
                  Angebot anfragen <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
